import { BPS_DENOMINATOR } from '../fixed-point';
import { assertSafeInteger } from '../integers';
import type { Rarity } from '../rarity';
import { EFFECT_MODES, type EffectMode } from './effect-modes';

/**
 * **As dezasseis CARTAS**, e nada mais que elas (gate V28H.B).
 *
 * ## O que uma carta é
 *
 * Uma HABILIDADE ATIVA. É a primeira coisa do jogo que o jogador dispara em vez
 * de possuir: uma joia está ligada enquanto estiver encaixada, um artefacto vale
 * por ser da conta, e uma carta só faz alguma coisa no instante em que é usada e
 * depois fica em espera. Três delas ficam equipadas ao mesmo tempo.
 *
 * ## O que este ficheiro é, e o que ele deliberadamente NÃO é
 *
 * É o CATÁLOGO: id, nome, raridade, onde vale e de quantos em quantos
 * milissegundos pode voltar a ser usada. Mais a aritmética do nível, que é a
 * única regra de progressão que as dezasseis partilham.
 *
 * Não é mecânica. Não há aqui dano, raio, duração, alvo, empurrão, vida de
 * veículo, cadência de summon nem probabilidade - e a ausência é o gate. Cada
 * uma dessas grandezas vive na simulação do modo que a executa, onde as unidades
 * são incompatíveis entre si: a caça mede a arena em 1280x720 unidades e o duelo
 * em micro-unidades inteiras de 4 800 000 x 3 000 000. Um raio escrito aqui teria
 * de estar errado num dos dois.
 *
 * Também não há inventário, socket, fusão, queda, atalho, instantânea, protocolo
 * nem arte. Tudo isso são gates seguintes, e cada um deles reverte sozinho
 * exatamente porque nenhum entrou aqui.
 *
 * ## O que já está decidido e está escrito
 *
 * - as dezasseis existem, com estes ids PERMANENTES;
 * - `CARD_MAX_LEVEL` é 6;
 * - cada nível acima do primeiro tira 12 pontos percentuais ao cooldown, e o
 *   sexto tira 60 - portanto restam sempre pelo menos 40% do original;
 * - **o upgrade só toca no cooldown.** Não aumenta dano, duração, raio, alcance,
 *   nem quantidade. É a decisão de equilíbrio mais forte deste sistema: uma
 *   carta de nível 6 é a mesma carta, mais vezes;
 * - oito valem só no duelo, e dizem-no.
 *
 * ## O que está registado como decisão FUTURA, e por isso não está aqui
 *
 * Ver `docs/gates/v28hb-cards-contract.md`. Em resumo: a Lança Solar é uma
 * exceção nominal e fechada à proibição geral de lasers e precisa de uma ADR
 * própria antes de qualquer mecânica; as joias da nave são ativadas pelos
 * ataques primários do Leviatã e do Drone, com linhagem fechada e sem permissão
 * booleana genérica; a arena do duelo cresce 5x em cada eixo e perde os
 * obstáculos antes de Catolé e Leviatã existirem; e a arte é vertical, 1024x1536
 * no master e 512x768 no derivado, sem nada gravado.
 */

/* -------------------------------------------------------------------------- */
/*  Progressão                                                                */
/* -------------------------------------------------------------------------- */

/**
 * **Seis, e o seis é o fim.**
 *
 * Não é um número de conveniência: ele e
 * {@link CARD_COOLDOWN_REDUCTION_PER_LEVEL_BPS} são as duas metades da mesma
 * decisão. Doze pontos por nível ao longo de cinco subidas dão exatamente
 * sessenta, e sessenta é o teto aprovado. Mexer num sem o outro parte a promessa
 * de que sobram 40% do cooldown.
 */
export const CARD_MAX_LEVEL = 6;

/**
 * **Doze pontos percentuais por nível acima do primeiro**, em basis points.
 *
 * Em basis points e não numa fração, pela mesma razão que toda a percentagem
 * deste jogo o é: `0,12` não é representável em binário, e cinco somas dele não
 * dão `0,6`. `1200` somado cinco vezes dá `6000`, exatamente, sempre.
 */
export const CARD_COOLDOWN_REDUCTION_PER_LEVEL_BPS = 1200;

/**
 * **O teto: 60%.** `1200 x 5` dá exatamente isto.
 *
 * Num nível legal este número nunca morde - ele é a mesma resposta pela segunda
 * porta. Fica como DEFESA, e a defesa que ele dá é a que interessa: com ela, o
 * cooldown que resta é `>= 4 000` bps do original por construção, e nenhuma
 * carta pode ser reduzida a zero por um nível que alguém escreveu a mais.
 *
 * Um teto sobre a REDUÇÃO de um cooldown é diferente de um teto sobre um
 * atributo concedido por um item, que o `CLAUDE.md` proíbe: nada aqui limita
 * dano, vida ou cadência. O que este número limita é a fração de uma espera que
 * deixa de acontecer, e a espera é a própria coisa que o nível compra.
 */
export const CARD_COOLDOWN_REDUCTION_MAX_BPS =
  CARD_COOLDOWN_REDUCTION_PER_LEVEL_BPS * (CARD_MAX_LEVEL - 1);

/**
 * **O que sobra no nível 6: 40% do cooldown original.**
 *
 * Escrito como a diferença e não como `4000`, para que continue a ser verdade no
 * dia em que alguém mexer no teto acima - e para que a prova que compara os dois
 * esteja a comparar duas coisas em vez de a repetir uma.
 */
export const CARD_COOLDOWN_FLOOR_BPS = BPS_DENOMINATOR - CARD_COOLDOWN_REDUCTION_MAX_BPS;

/**
 * **A versão do CONTEÚDO das cartas.**
 *
 * Sobe quando o catálogo muda de maneira que altere o que um jogador tem: um
 * cooldown-base diferente, um modo diferente, uma carta nova, uma carta retirada.
 * Não sobe por um nome, um comentário ou uma raridade de etiqueta.
 *
 * Existe pela mesma razão que `JEWEL_CONTENT_VERSION` e
 * `ARTIFACT_CONTENT_VERSION` existem: uma impressão digital de loadout tem de
 * mudar quando o conteúdo por trás dela muda, ou uma amostra medida numa
 * economia financia outra.
 *
 * **1 -> 2 (gate V28I.C): o Bastião passou a SOMENTE PVP.**
 *
 * É exactamente o caso que a regra acima nomeia - "um modo diferente" - e é o
 * caso mais forte dela: um loadout com o Bastião vale hoje menos numa caçada do
 * que valia ontem, e uma amostra económica medida com a versão antiga estaria a
 * financiar uma carta que já não faz nada nesse modo.
 */
export const CARD_CONTENT_VERSION = 2;

/**
 * A redução do cooldown deste nível, em basis points.
 *
 * ## Porque níveis acima de seis são ACEITES e não recusados
 *
 * Porque um nível acima do teto não é uma entrada malformada - é uma pergunta
 * legítima sobre um domínio onde a resposta é constante, e recusá-la obrigaria
 * cada chamador a fazer o `min` antes de perguntar. O teto responde: seis, sete,
 * trinta e `MAX_SAFE_INTEGER` dão todos `6000`.
 *
 * ## E porque zero, negativos e frações são RECUSADOS
 *
 * Porque esses não são perguntas: são estados que não existem. Não há carta de
 * nível zero, de nível `-1` nem de nível `1,5`, e o `CLAUDE.md` é explícito -
 * um valor malformado é recusado, nunca silenciosamente corrigido com
 * `Math.trunc` ou `Math.floor`.
 */
export function cardCooldownReductionBps(level: number): number {
  assertSafeInteger(level, 'card level');
  if (level < 1) {
    throw new RangeError(`a card level starts at 1, got ${level}`);
  }
  const reduction = CARD_COOLDOWN_REDUCTION_PER_LEVEL_BPS * (level - 1);
  return Math.min(reduction, CARD_COOLDOWN_REDUCTION_MAX_BPS);
}

/**
 * **O cooldown deste nível, em milissegundos inteiros.**
 *
 * ## Uma multiplicação, uma truncagem, e em `bigint`
 *
 * `base x (10000 - reducao) / 10000`, avaliado em `bigint` e truncado UMA vez,
 * no fim. Em `bigint` porque um produto intermédio de um cooldown longo por
 * `10 000` não tem de caber em coisa nenhuma, e porque `base * 0,88` em vírgula
 * flutuante daria um número que depois alguém arredondaria - e um arredondamento
 * que acontece duas vezes acontece de maneiras diferentes em máquinas diferentes.
 *
 * Truncar, e não arredondar: um cooldown truncado é mais curto, nunca mais longo,
 * portanto o erro cai sempre do lado do jogador e nunca do lado de uma carta que
 * demora mais do que a tabela promete.
 *
 * No nível 6 a conta é `base x 4000 / 10000`. Os dezasseis cooldowns-base são
 * múltiplos de mil milissegundos, portanto ali a divisão é exata e o resultado é
 * exatamente dois quintos do original.
 */
export function cardCooldownMsAtLevel(baseCooldownMs: number, level: number): number {
  assertSafeInteger(baseCooldownMs, 'card base cooldown');
  if (baseCooldownMs <= 0) {
    throw new RangeError(
      `a card cooldown is a positive number of milliseconds, got ${baseCooldownMs}`,
    );
  }
  const remainingBps = BPS_DENOMINATOR - cardCooldownReductionBps(level);
  const scaled = (BigInt(baseCooldownMs) * BigInt(remainingBps)) / BigInt(BPS_DENOMINATOR);
  return Number(scaled);
}

/** O cooldown desta carta neste nível. O mesmo cálculo, pela definição. */
export function cardCooldownMs(definition: CardDefinition, level: number): number {
  return cardCooldownMsAtLevel(definition.baseCooldownMs, level);
}

/* -------------------------------------------------------------------------- */
/*  O catálogo                                                                */
/* -------------------------------------------------------------------------- */

/**
 * **Os dezasseis ids, PERMANENTES e por esta ordem.**
 *
 * Permanentes porque um id de carta vai ser escrito em linhas de inventário, em
 * quedas, em bilhetes assinados e em instantâneas: renomear um seria perder o
 * que os jogadores têm. Um nome de exibição muda; um id não.
 *
 * A ordem é a do contrato aprovado, e é ela que a interface segue quando mostra
 * o catálogo inteiro. Não é ordem de raridade nem de força.
 */
export const CARD_IDS = [
  'card_dash',
  'card_speed_burst',
  'card_solar_lance',
  'card_charged_fireball',
  'card_leviathan',
  'card_blade_storm',
  'card_catole',
  'card_attack_drone',
  'card_malware',
  'card_freeze',
  'card_slow',
  'card_attack_break',
  'card_damage_break',
  'card_cloak',
  'card_bastion',
  'card_super_cannons',
] as const;

export type CardId = (typeof CARD_IDS)[number];

/** O que o catálogo sabe de uma carta. Tudo o resto é de outro gate. */
export interface CardDefinition {
  readonly definitionId: CardId;
  /**
   * O nome aprovado, em português.
   *
   * Ao contrário das joias de socket - que ficaram deliberadamente sem nome
   * porque o conteúdo delas não estava decidido - as dezasseis cartas têm nome
   * fechado. `Catolé` é o único que não pode mudar por decisão explícita do dono.
   */
  readonly displayName: string;
  readonly rarity: Rarity;
  /**
   * Onde ela produz efeito. Nunca vazio: uma carta que não valesse em modo nenhum
   * não seria uma carta pendente, seria uma carta que não existe.
   */
  readonly supportedModes: readonly EffectMode[];
  /** O cooldown do nível 1, em milissegundos inteiros. */
  readonly baseCooldownMs: number;
  /** Seis, para as dezasseis. O campo existe para quem lê uma definição solta. */
  readonly maxLevel: typeof CARD_MAX_LEVEL;
}

/** Vale nos dois sítios. Uma referência partilhada: dezasseis não precisam de dez arrays. */
const BOTH_MODES: readonly EffectMode[] = Object.freeze([...EFFECT_MODES]);
/** Só no duelo. */
const PVP_ONLY: readonly EffectMode[] = Object.freeze(['pvp']);

/** Um segundo, em milissegundos. Para a tabela abaixo se ler como o contrato. */
const SECOND_MS = 1_000;

/**
 * A tabela aprovada, indexada pelo id.
 *
 * `Record<CardId, ...>` e não um array de objetos: assim o COMPILADOR exige as
 * dezasseis entradas e recusa uma décima sétima. Um id acrescentado a
 * {@link CARD_IDS} sem linha aqui é erro de compilação, e não uma carta que
 * chega a produção sem cooldown.
 */
const CATALOG: Readonly<Record<CardId, Omit<CardDefinition, 'definitionId' | 'maxLevel'>>> =
  Object.freeze({
    card_dash: {
      displayName: 'Arranque',
      rarity: 'common',
      supportedModes: PVP_ONLY,
      baseCooldownMs: 8 * SECOND_MS,
    },
    card_speed_burst: {
      displayName: 'Sobremarcha',
      rarity: 'common',
      supportedModes: PVP_ONLY,
      baseCooldownMs: 20 * SECOND_MS,
    },
    card_solar_lance: {
      displayName: 'Lança Solar',
      rarity: 'uncommon',
      supportedModes: BOTH_MODES,
      baseCooldownMs: 18 * SECOND_MS,
    },
    card_charged_fireball: {
      displayName: 'Esfera Ígnea',
      rarity: 'uncommon',
      supportedModes: BOTH_MODES,
      baseCooldownMs: 22 * SECOND_MS,
    },
    card_leviathan: {
      displayName: 'Leviatã Aéreo',
      rarity: 'prismatic',
      supportedModes: BOTH_MODES,
      baseCooldownMs: 90 * SECOND_MS,
    },
    card_blade_storm: {
      displayName: 'Turbilhão de Lâminas',
      rarity: 'excellent',
      supportedModes: BOTH_MODES,
      baseCooldownMs: 30 * SECOND_MS,
    },
    card_catole: {
      displayName: 'Catolé',
      rarity: 'anomaly',
      supportedModes: BOTH_MODES,
      baseCooldownMs: 60 * SECOND_MS,
    },
    card_attack_drone: {
      displayName: 'Drone de Ataque',
      rarity: 'excellent',
      supportedModes: BOTH_MODES,
      baseCooldownMs: 40 * SECOND_MS,
    },
    card_malware: {
      displayName: 'Malware',
      rarity: 'epic',
      supportedModes: PVP_ONLY,
      baseCooldownMs: 35 * SECOND_MS,
    },
    card_freeze: {
      displayName: 'Congelamento',
      rarity: 'legendary',
      supportedModes: PVP_ONLY,
      baseCooldownMs: 45 * SECOND_MS,
    },
    card_slow: {
      displayName: 'Lentidão',
      rarity: 'rare',
      supportedModes: PVP_ONLY,
      baseCooldownMs: 25 * SECOND_MS,
    },
    card_attack_break: {
      displayName: 'Curto-Circuito',
      rarity: 'rare',
      supportedModes: PVP_ONLY,
      baseCooldownMs: 25 * SECOND_MS,
    },
    card_damage_break: {
      displayName: 'Marca da Caveira',
      rarity: 'epic',
      supportedModes: PVP_ONLY,
      baseCooldownMs: 30 * SECOND_MS,
    },
    card_cloak: {
      displayName: 'Manto Espectral',
      rarity: 'legendary',
      supportedModes: PVP_ONLY,
      baseCooldownMs: 40 * SECOND_MS,
    },
    card_bastion: {
      displayName: 'Bastião',
      rarity: 'perfect',
      /*
       * **SOMENTE PVP** (gate V28I.C), por decisão do dono.
       *
       * O que ela concede - nenhum projéctil aterra durante quatro segundos - é
       * uma resposta a tiros APONTADOS À MÃO por outra pessoa. Numa caçada os
       * tiros vêm de inimigos guiados pela simulação, e a carta passava a ser
       * quatro segundos de imunidade sem ninguém do outro lado a poder reagir.
       *
       * O jogador continua a possuí-la, a subi-la de nível e a equipá-la: o que
       * muda é onde ela vale. É a etiqueta `SOMENTE PVP` que o diz na bancada,
       * e é a mesma que o Arranque e a Sobremarcha já usavam.
       */
      supportedModes: PVP_ONLY,
      baseCooldownMs: 50 * SECOND_MS,
    },
    card_super_cannons: {
      displayName: 'Super Canhões',
      rarity: 'perfect',
      supportedModes: BOTH_MODES,
      baseCooldownMs: 35 * SECOND_MS,
    },
  });

/**
 * As dezasseis, congeladas, pela ordem de {@link CARD_IDS}.
 *
 * Construída da lista de ids e não escrita à mão uma segunda vez: assim a ordem
 * tem uma casa só, e nenhuma entrada pode existir sem id nem um id sem entrada.
 */
export const CARD_DEFINITIONS: readonly CardDefinition[] = Object.freeze(
  CARD_IDS.map((definitionId) =>
    Object.freeze({
      definitionId,
      maxLevel: CARD_MAX_LEVEL,
      ...CATALOG[definitionId],
    }),
  ),
);

const BY_ID: ReadonlyMap<string, CardDefinition> = new Map(
  CARD_DEFINITIONS.map((card) => [card.definitionId, card]),
);

/** Nenhum modo. Uma referência partilhada, para um id desconhecido não alocar. */
const NO_MODES: readonly EffectMode[] = Object.freeze([]);

/**
 * A definição, ou `null`.
 *
 * `null` e não uma exceção, pela mesma razão que {@link cardSupportedModes} não
 * atira: a interface desenha o que o servidor lhe deu, e uma carta que este build
 * não conhece não deve derrubar um painel de inventário.
 */
export function cardDefinition(definitionId: string): CardDefinition | null {
  return BY_ID.get(definitionId) ?? null;
}

/** Se este id é uma das dezasseis. */
export function isCardDefinitionId(definitionId: string): definitionId is CardId {
  return BY_ID.has(definitionId);
}

/**
 * Onde esta carta produz efeito.
 *
 * Devolve a lista e não um booleano porque as duas perguntas existem: quem quer
 * saber "vale na caça" pergunta {@link cardWorksIn}, e quem quer mostrar a lista
 * tem-na. Um id desconhecido devolve vazio.
 */
export function cardSupportedModes(definitionId: string): readonly EffectMode[] {
  return BY_ID.get(definitionId)?.supportedModes ?? NO_MODES;
}

/** Se esta carta produz efeito NESTE modo. */
export function cardWorksIn(definitionId: string, mode: EffectMode): boolean {
  return cardSupportedModes(definitionId).includes(mode);
}

/**
 * **Se esta carta é exclusivamente de DUELO**: vale no PvP e não vale na caça.
 *
 * As duas metades da pergunta são feitas, e não só a segunda: `!cardWorksIn(id,
 * 'hunt')` sozinho seria verdadeiro para um id desconhecido, e anunciar
 * exclusividade de duelo a uma carta que este build não conhece seria prometer
 * um efeito que ela pode não ter.
 *
 * É esta função, e nenhuma lista de ids escrita num componente, que decide se a
 * interface mostra o aviso.
 */
export function isPvpOnlyCard(definitionId: string): boolean {
  return cardWorksIn(definitionId, 'pvp') && !cardWorksIn(definitionId, 'hunt');
}
